import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Divider, Form, InputNumber, Modal, Row, Space, Spin, Typography } from 'antd';
import { EyeOutlined, FilePdfOutlined } from '@ant-design/icons';
import { PDFViewer } from '@react-pdf/renderer';
import { useGenerateLoanScheduleFromConfig } from '@/shared/api/client/loan-schedule';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useCompany } from '@/hooks/company/use-company';
import { useProductPropertiesById } from '../hooks/useProductProperties';
// import LoanFeeBreakdown from '../../loans/components/application/LoanFeeBreakdown'; // TODO: Component not implemented yet
// import LoanDeductionFormDocument from '../../loans/components/LoanDeductionFormDocument'; // TODO: Component not implemented yet
// import SmartLoanApplicationDocument from '../../loans/components/SmartLoanApplicationDocument'; // TODO: Component not implemented yet
// import { useLoanFeeCalculation } from '../../loans/hooks/useLoanFeeCalculation'; // TODO: Hook not implemented yet
// import { HAZINA_DEDUCTION_FORM_CONFIG } from '../../loans/components/DeductionFormConfig'; // TODO: Component not implemented yet
// import useLoanFormTemplate from '../../loans/hooks/useLoanFormTemplate'; // TODO: Hook not implemented yet

// Stub implementations for missing loans module
const HAZINA_DEDUCTION_FORM_CONFIG: any = {};
const useLoanFeeCalculation: any = () => ({ data: null, isLoading: false });
const useLoanFormTemplate: any = () => ({ formConfig: null });
const LoanFeeBreakdown: React.FC<any> = () => <div>LoanFeeBreakdown not implemented</div>;
const LoanDeductionFormDocument: React.FC<any> = () => <div>LoanDeductionFormDocument not implemented</div>;
const SmartLoanApplicationDocument: React.FC<any> = () => <div>SmartLoanApplicationDocument not implemented</div>;

const useLoanBalanceCheck: any = () => ({ data: null, isLoading: false });

const { Text } = Typography;

interface LoanProductPreviewPanelProps {
    glNo: string;
    productName: string;
    baseInterestRate?: number;
    defaultAmount?: number;
    defaultTerm?: number;
    requiresDeductionForm?: boolean;
}

const LoanProductPreviewPanel: React.FC<LoanProductPreviewPanelProps> = ({
    glNo,
    productName,
    baseInterestRate,
    defaultAmount,
    defaultTerm,
    requiresDeductionForm,
}) => {
    const [form] = Form.useForm();
    const authUser = useAuthUser();
    const { data: companyData } = useCompany();
    const [previewAmount, setPreviewAmount] = useState<number | null>(defaultAmount || null);
    const [previewPeriod, setPreviewPeriod] = useState<number | null>(defaultTerm || null);
    const [isApplicationFormVisible, setIsApplicationFormVisible] = useState(false);
    const [isDeductionFormVisible, setIsDeductionFormVisible] = useState(false);
    const [hasTriggeredPreview, setHasTriggeredPreview] = useState(false);

    useEffect(() => {
        setPreviewAmount(defaultAmount || null);
        form.setFieldValue('amount', defaultAmount || null);
    }, [defaultAmount]);

    useEffect(() => {
        setPreviewPeriod(defaultTerm || null);
        form.setFieldValue('period', defaultTerm || null);
    }, [defaultTerm]);

    // Fetch fee breakdown for preview
    const { data: feeBreakdown } = useLoanFeeCalculation(
        glNo,
        previewAmount && hasTriggeredPreview ? previewAmount : undefined,
        previewPeriod && hasTriggeredPreview ? previewPeriod : undefined
    );

    // Fetch product properties for installment rounding precision
    const { data: productPropertiesResponse } = useProductPropertiesById(glNo);
    const productProperties = productPropertiesResponse;
    const installmentRoundingPrecision: number = (productPropertiesResponse as any)?.installmentRoundingPrecision ?? 2;
    // const { formConfig } = useLoanFormTemplate(productProperties); // TODO: Hook not implemented yet
    const formConfig: any = null; // Stub for missing formConfig
    const previewFormConfig = useMemo(() => {
        if (!formConfig) {
            return {
                header: {
                    showLogo: true,
                    formTitle: 'LOAN APPLICATION FORM',
                },
                branding: {
                    logoUrl: '/public/appearance/logo',
                    watermarkUrl: '/public/appearance/logo',
                    showWatermark: true,
                },
            } as any;
        }

        return {
            ...formConfig,
            header: {
                ...(formConfig.header || {}),
                showLogo: formConfig.header?.showLogo ?? true,
            },
            branding: {
                ...(formConfig.branding || {}),
                logoUrl: formConfig.branding?.logoUrl || '/public/appearance/logo',
                watermarkUrl: formConfig.branding?.watermarkUrl || formConfig.branding?.logoUrl || '/public/appearance/logo',
                showWatermark: formConfig.branding?.showWatermark ?? true,
            },
        } as any;
    }, [formConfig]);

    // Fetch schedule from product config (uses backend product settings)
    const {
        data: scheduleData,
        isLoading: isScheduleLoading,
    } = useGenerateLoanScheduleFromConfig(
        { glNo, amount: previewAmount || 0, period: previewPeriod || 0 },
        {
            query: {
                enabled: hasTriggeredPreview && !!previewAmount && !!previewPeriod,
            },
        }
    );

    const handlePreview = () => {
        form.validateFields().then(values => {
            setPreviewAmount(values.amount);
            setPreviewPeriod(values.period);
            setHasTriggeredPreview(true);
        });
    };

    const scheduleResult = scheduleData;
    const firstInstallment = scheduleResult?.scheduleDTO?.[0];
    const installmentAmount = firstInstallment
        ? ((firstInstallment.principal ?? 0) + (firstInstallment.interest ?? 0))
        : undefined;

    return (
        <div className="space-y-4">
            <Alert
                type="info"
                message="Schedule Preview"
                description="Enter loan parameters below to preview the repayment schedule and deduction form using the same components customers see during the application process. This preview uses the product's configured calculation method, interest rate, and rounding settings."
                showIcon
                className="mb-4"
            />

            <Card title="Preview Parameters">
                <Form form={form} layout="vertical" initialValues={{ amount: defaultAmount, period: defaultTerm }}>
                    <Row gutter={16}>
                        <Col xs={24} sm={10}>
                            <Form.Item
                                name="amount"
                                label="Loan Amount"
                                rules={[{ required: true, message: 'Enter loan amount' }]}
                            >
                                <InputNumber
                                    min={1}
                                    style={{ width: '100%' }}
                                    placeholder="e.g. 2500000"
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    // @ts-ignore
                                    parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={10}>
                            <Form.Item
                                name="period"
                                label="Loan Period (months)"
                                rules={[{ required: true, message: 'Enter loan period' }]}
                            >
                                <InputNumber
                                    min={1}
                                    max={360}
                                    style={{ width: '100%' }}
                                    placeholder="e.g. 12"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={4} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
                            <Button
                                type="primary"
                                icon={<EyeOutlined />}
                                onClick={handlePreview}
                                block
                            >
                                Preview
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {isScheduleLoading && (
                <div className="flex justify-center py-8">
                    <Spin size="large" />
                </div>
            )}

            {hasTriggeredPreview && scheduleResult && !isScheduleLoading && (
                <>
                    <Divider orientation="left">Repayment Schedule Preview</Divider>

                    <Card className="bg-blue-50 mb-4" bodyStyle={{ padding: '12px' }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <Text type="secondary">Loan Amount</Text>
                                <div className="text-lg font-semibold">
                                    {(previewAmount || 0).toLocaleString('en-US', { style: 'currency', currency: 'TZS' })}
                                </div>
                            </div>
                            <div>
                                <Text type="secondary">Total Interest</Text>
                                <div className="text-lg font-semibold">
                                    {scheduleResult.totalInterest?.toLocaleString('en-US', { style: 'currency', currency: 'TZS' })}
                                </div>
                            </div>
                            <div>
                                <Text type="secondary">Total Repayment</Text>
                                <div className="text-lg font-semibold">
                                    {((scheduleResult.totalInterest || 0) + (previewAmount || 0)).toLocaleString('en-US', { style: 'currency', currency: 'TZS' })}
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Reuse LoanScheduleComponent directly with schedule data */}
                    <SchedulePreviewTable scheduleData={scheduleResult} precision={installmentRoundingPrecision} />

                    {/* Fee Breakdown */}
                    {previewAmount && previewPeriod && (
                        <>
                            <Divider orientation="left">Fee Breakdown Preview</Divider>
                            <LoanFeeBreakdown
                                glNo={glNo}
                                loanAmount={previewAmount}
                                loanPeriod={previewPeriod}
                            />
                        </>
                    )}

                    <Divider orientation="left">Application Form Preview</Divider>
                    <Space>
                        <Button
                            type="primary"
                            icon={<FilePdfOutlined />}
                            onClick={() => setIsApplicationFormVisible(true)}
                        >
                            Preview Application Form
                        </Button>
                    </Space>

                    {/* Deduction Form Preview */}
                    {requiresDeductionForm && (
                        <>
                            <Divider orientation="left">Deduction Form Preview</Divider>
                            <Space>
                                <Button
                                    icon={<FilePdfOutlined />}
                                    onClick={() => setIsDeductionFormVisible(true)}
                                >
                                    Preview Deduction Form
                                </Button>
                            </Space>
                        </>
                    )}
                </>
            )}

            {isApplicationFormVisible && scheduleResult && (
                <Modal
                    title="Loan Application Form Preview"
                    open={isApplicationFormVisible}
                    onCancel={() => setIsApplicationFormVisible(false)}
                    footer={null}
                    width="80%"
                    style={{ top: 20 }}
                    bodyStyle={{ height: '80vh', padding: 0 }}
                >
                    <PDFViewer width="100%" height="100%" style={{ border: 'none' }}>
                        <SmartLoanApplicationDocument
                            user={authUser}
                            company={companyData}
                            product={productProperties || { productName, glNo, baseInterestRate }}
                            amount={previewAmount?.toString()}
                            period={previewPeriod?.toString()}
                            scheduleData={scheduleResult}
                            feeBreakdown={feeBreakdown?.data}
                            formConfig={previewFormConfig}
                        />
                    </PDFViewer>
                </Modal>
            )}

            {/* Deduction Form Modal */}
            {isDeductionFormVisible && scheduleResult && (
                <Modal
                    title="Loan Deduction Form Preview"
                    open={isDeductionFormVisible}
                    onCancel={() => setIsDeductionFormVisible(false)}
                    footer={null}
                    width="80%"
                    style={{ top: 20 }}
                    bodyStyle={{ height: '80vh', padding: 0 }}
                >
                    <PDFViewer width="100%" height="100%" style={{ border: 'none' }}>
                        <LoanDeductionFormDocument
                            user={authUser}
                            company={companyData}
                            product={{ productName, glNo }}
                            amount={previewAmount?.toString()}
                            period={previewPeriod?.toString()}
                            interestRate={baseInterestRate}
                            installment={installmentAmount}
                            maturityDate={scheduleResult.expireDate}
                            disbursementDate={scheduleResult.disbursementDate}
                            deductionFormConfig={HAZINA_DEDUCTION_FORM_CONFIG}
                        />
                    </PDFViewer>
                </Modal>
            )}
        </div>
    );
};

/**
 * Inner component to render schedule table directly from data (no API call needed).
 * Mirrors the display used in LoanScheduleComponent.
 */
const SchedulePreviewTable: React.FC<{ scheduleData: any; precision?: number }> = ({ scheduleData, precision = 2 }) => {
    const [expandedView, setExpandedView] = useState(false);
    const scheduleDTO = scheduleData?.scheduleDTO || [];

    const summaryData = scheduleDTO.length <= 3
        ? scheduleDTO
        : [scheduleDTO[0], scheduleDTO[Math.floor(scheduleDTO.length / 2)], scheduleDTO[scheduleDTO.length - 1]];

    const displayData = expandedView ? scheduleDTO : summaryData;

    const hasFees = scheduleDTO.some((s: any) =>
        (s.applicationFee && s.applicationFee !== 0) ||
        (s.processingFee  && s.processingFee  !== 0) ||
        (s.commissionFee  && s.commissionFee  !== 0) ||
        (s.commitmentFee  && s.commitmentFee  !== 0)
    );

    const formatAmt = (amount: number | null | undefined) => {
        if (amount == null) return '-';
        return amount.toLocaleString('en-US', {
            style: 'currency', currency: 'TZS',
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        });
    };

    const tdStyle = { padding: '5px 8px', border: '1px solid #e0e0e0' };
    const tdRStyle = { ...tdStyle, textAlign: 'right' as const };
    const thStyle = { padding: '6px 8px', textAlign: 'right' as const, border: '1px solid #e0e0e0' };
    const thLStyle = { padding: '6px 8px', textAlign: 'left' as const, border: '1px solid #e0e0e0' };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text strong>{expandedView ? 'Full Schedule' : 'Summary View (first, middle, last)'}</Text>
                <Button size="small" onClick={() => setExpandedView(v => !v)}>
                    {expandedView ? 'Show Summary' : 'Show Full Schedule'}
                </Button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                    <tr style={{ background: '#f0f0f0' }}>
                        <th style={thLStyle}>#</th>
                        <th style={thLStyle}>Date</th>
                        <th style={thStyle}>Principal</th>
                        <th style={thStyle}>Interest</th>
                        {hasFees && <>
                            <th style={thStyle}>App. Fee</th>
                            <th style={thStyle}>Proc. Fee</th>
                            <th style={thStyle}>Comm. Fee</th>
                            <th style={thStyle}>Commit. Fee</th>
                        </>}
                        <th style={{ ...thStyle, fontWeight: 'bold' }}>Total Due</th>
                        <th style={thStyle}>Running Principal</th>
                    </tr>
                </thead>
                <tbody>
                    {displayData.map((row: any, idx: number) => (
                        <tr key={row.trxDate + idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9ff' }}>
                            <td style={tdStyle}>
                                {expandedView ? idx + 1 : [1, Math.floor(scheduleDTO.length / 2) + 1, scheduleDTO.length][idx]}
                            </td>
                            <td style={tdStyle}>
                                {new Date(row.trxDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td style={tdRStyle}>{formatAmt(row.principal)}</td>
                            <td style={tdRStyle}>{formatAmt(row.interest)}</td>
                            {hasFees && <>
                                <td style={tdRStyle}>{row.applicationFee ? formatAmt(row.applicationFee) : '-'}</td>
                                <td style={tdRStyle}>{row.processingFee  ? formatAmt(row.processingFee)  : '-'}</td>
                                <td style={tdRStyle}>{row.commissionFee  ? formatAmt(row.commissionFee)  : '-'}</td>
                                <td style={tdRStyle}>{row.commitmentFee  ? formatAmt(row.commitmentFee)  : '-'}</td>
                            </>}
                            <td style={{ ...tdRStyle, fontWeight: 'bold' }}>
                                {formatAmt(row.totalDue ?? (row.principal + row.interest))}
                            </td>
                            <td style={tdRStyle}>{formatAmt(row.runningPrinciple)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {!expandedView && scheduleDTO.length > 3 && (
                <div style={{ textAlign: 'center', marginTop: 6, color: '#888', fontSize: 12 }}>
                    Showing 3 of {scheduleDTO.length} installments.{' '}
                    <Button type="link" size="small" onClick={() => setExpandedView(true)}>Show all</Button>
                </div>
            )}
        </div>
    );
};

export default LoanProductPreviewPanel;
